const STORAGE_KEY = 'portal_offline_return_path';
const MAX_LEN = 512;
const APP_BASE = '/front';

/** 再試行先として保存しないパス */
const RETURN_PATH_EXCLUDED_PREFIXES = ['/error'];

let offlineErrorRedirectPending = false;

function isSafeRedirectPath(fullPath) {
  if (!fullPath || typeof fullPath !== 'string') return false;
  if (fullPath.length > MAX_LEN) return false;
  if (!fullPath.startsWith('/') || fullPath.startsWith('//')) return false;
  const pathOnly = fullPath.split(/[?#]/)[0];
  if (pathOnly.includes('..')) return false;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(pathOnly)) return false;
  return true;
}

function isReturnPathExcluded(pathOnly) {
  return RETURN_PATH_EXCLUDED_PREFIXES.some(
    (excluded) => pathOnly === excluded || pathOnly.startsWith(`${excluded}/`)
  );
}

/**
 * エラー画面のパスかどうか
 * @param {string} path
 * @returns {boolean}
 */
export function isErrorPath(path) {
  return path === '/error' || path.startsWith('/error/');
}

/**
 * Axios のネットワークエラーかどうか（接続失敗・タイムアウト含む）
 * @param {unknown} error
 * @returns {boolean}
 */
export function isNetworkError(error) {
  if (!error || error.response) return false;
  return (
    error.code === 'ERR_NETWORK'
    || error.code === 'ECONNABORTED'
    || error.message === 'Network Error'
  );
}

/**
 * オフライン再試行時に戻る Vue Router のパスを保存する
 * @param {string} fullPath
 */
export function setOfflineReturnPath(fullPath) {
  if (!isSafeRedirectPath(fullPath)) return;
  const pathOnly = fullPath.split(/[?#]/)[0];
  if (isReturnPathExcluded(pathOnly)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, fullPath);
  } catch (_) {
    // ignore
  }
}

/**
 * 保存済みの遷移先を取り出して削除する
 * @param {import('vue-router').Router} router
 * @returns {string | null}
 */
export function consumeOfflineReturnPath(router) {
  let raw = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // ignore
  }
  if (!isSafeRedirectPath(raw)) return null;
  const resolved = router.resolve(raw);
  if (isReturnPathExcluded(resolved.path)) return null;
  return raw;
}

/**
 * Vue Router のパスをフルナビゲーション用 URL に変換する
 * @param {string} routerPath
 * @returns {string}
 */
export function toAppUrl(routerPath) {
  const path = routerPath.startsWith('/') ? routerPath : `/${routerPath}`;
  return `${APP_BASE}${path}`;
}

/**
 * オフライン／ネットワークエラー時にエラー画面へ遷移する
 * @param {import('vue-router').Router} router
 * @param {string} [returnPath]
 */
export async function redirectToOfflineError(router, returnPath) {
  if (offlineErrorRedirectPending) return;

  const current = router.currentRoute.value;
  if (isErrorPath(current.path)) return;

  offlineErrorRedirectPending = true;
  try {
    if (returnPath) {
      setOfflineReturnPath(returnPath);
    }
    await router.replace({
      path: '/error',
      query: { errorCode: 'offline' },
    });
  } finally {
    offlineErrorRedirectPending = false;
  }
}
